import { DataGridPremium, GridActionsCellItem, type GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import type React from 'react'
import { useRef, useState } from 'react'
import { useAccountingYears, useDeleteAccountingYear } from '../api/react-query'
import type { AccountXYear } from '../api/swagger/definitions/backoffice'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Spacings from '../figma/tokens/Spacings'
import useLanguage from '../flamingo/hooks/useLanguage'
import { getYearsTableActions } from '../helpers/AccountingTablesHelper'
import { TableHeader, useTableGridProps } from '../helpers/getTableOptions'
import { captureException } from '../helpers/sentryHelpers'
import FigmaButton from '../mynt-components/components/FigmaButton'
import ImageContainer from '../mynt-components/components/ImageContainer'
import MyntPopper from '../mynt-components/components/MyntPopper'
import { getText, TextKeys } from '../tiger/libs/TextRepository'
import { YearFormTypes } from './ModalAccountingYear'
import { ModalAccountingYear, ModalConfirmDialog } from './Modals'
import TableWrapper from './TableWrapper'

enum YearDefaultState {
  TRUE = 'True',
  FALSE = 'False',
}

const YearsTable: React.FC = () => {
  const language = useLanguage()
  const [yearValues, setYearValues] = useState<AccountXYear>()
  const [isYearModalOpen, setIsYearModalOpen] = useState<boolean>(false)
  const [isYearDeleteModalOpen, setIsYearDeleteModalOpen] = useState<boolean>(false)
  const divRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(divRef.current)

  const { data: years = [], isLoading: yearsLoading } = useAccountingYears({
    refetchOnWindowFocus: false,
  })
  const deleteMutation = useDeleteAccountingYear()

  const gridProps = useTableGridProps({ headerTextKey: TextKeys.accountingPaymentsOCR })

  const apiRef = useGridApiRef()

  const handleDeleteClick = (year: AccountXYear) => {
    setIsYearDeleteModalOpen(true)
    setYearValues(year)
  }

  const handleEditClick = (year: AccountXYear) => {
    setIsYearModalOpen(true)
    setYearValues(year)
  }

  const rowActions = getYearsTableActions(handleEditClick, handleDeleteClick, setAnchorEl)

  const columns: GridColDef<AccountXYear>[] = [
    { headerName: getText(TextKeys.createYearNameLabel, language), field: 'name', flex: 1 },
    { headerName: getText(TextKeys.createYearFromDateLabel, language), field: 'fromDate', flex: 1 },
    { headerName: getText(TextKeys.createYearToDateLabel, language), field: 'toDate', flex: 1 },
    {
      headerName: getText(TextKeys.yearsDefaultLabel, language),
      field: 'default',
      flex: 1,
      valueGetter: (_, row) => (row.default ? YearDefaultState.TRUE : YearDefaultState.FALSE),
    },
    {
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) =>
        rowActions.map((action, index) => (
          <GridActionsCellItem
            key={index === 0 ? 'edit' : 'delete'}
            icon={<ImageContainer imageKey={action.iconProps.imageKey} />}
            label={index === 0 ? 'Edit' : 'Delete'}
            onClick={(event) => action.onClick(event, row)}
          />
        )),
    },
  ]

  const handleDeleteAccept = async () => {
    handleClearModal()
    return deleteMutation
      .mutateAsync(yearValues?.id as string)
      .then((res) => res)
      .catch((err) => captureException(err))
  }

  const handleClearModal = () => {
    setIsYearDeleteModalOpen(false)
    setYearValues(undefined)
  }

  return (
    <>
      <ModalAccountingYear
        isOpen={isYearModalOpen}
        type={yearValues ? YearFormTypes.EDIT : YearFormTypes.CREATE}
        values={yearValues}
        onModalCancel={() => {
          setIsYearModalOpen(false)
          setYearValues(undefined)
        }}
      />
      <MyntPopper
        visible={isYearDeleteModalOpen}
        noPadding
        marginBottom={Spacings.large}
        position="top"
        anchorRef={anchorEl}
        content={<ModalConfirmDialog onCancel={handleClearModal} onConfirm={handleDeleteAccept} />}
      />
      <TableWrapper>
        <TableHeader
          title={getText(TextKeys.yearsHeading, language)}
          apiRef={apiRef}
          actions={<FigmaButton textKey={TextKeys.accountingYearsCreateYears} onClick={() => setIsYearModalOpen(true)} />}
        />
        <DataGridPremium
          {...gridProps}
          apiRef={apiRef}
          rows={years}
          columns={columns}
          loading={yearsLoading}
          sx={{
            ...gridProps.sx,
            '& .MuiDataGrid-columnHeader': { backgroundColor: ColorsDeprecated.surfaceOnWhite },
          }}
        />
      </TableWrapper>
    </>
  )
}

export default YearsTable
